<script setup>
import { computed, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePwaInstall } from 'dashboard/composables/usePwaInstall';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';
import MelhoriaModal from './MelhoriaModal.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useSidebarDropdownAnchor } from './useSidebarDropdownAnchor';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

// Menu fica no rodapé da sidebar, então abre pra cima.
const { triggerRef, anchorStyle, menuMaxHeight, measure } =
  useSidebarDropdownAnchor({ placement: 'top' });

// SiteUp E4.3/fix: o scale-in do DropdownContainer não alcança este menu —
// ele é teleportado pro <body>, e o Transition do container anima uma casca
// vazia; a animação roda aqui. O isOpen vem como PROP DO SLOT default (ver
// template): useDropdownContext() aqui no pai do container explodia no setup
// (inject não enxerga provide de um filho) e derrubava o componente inteiro.
const showMelhoria = ref(false);

const { canInstall, promptInstall } = usePwaInstall();

// Documentação privada SiteUp: abre a página de login e já entrega a senha
// de acesso (copiada pro clipboard + mostrada no toast) pra facilitar a vida.
const DOCS_URL = 'https://www.siteup.com.br/docs/login?redirect=%2Fdocs';
const DOCS_PASSWORD = 'Senha123!';

const openDocs = async () => {
  try {
    await navigator.clipboard.writeText(DOCS_PASSWORD);
    useAlert(`Senha copiada: ${DOCS_PASSWORD} — cole na página de login.`);
  } catch (error) {
    useAlert(`Senha de acesso à documentação: ${DOCS_PASSWORD}`);
  }
  window.open(DOCS_URL, '_blank', 'noopener');
};

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-ri-lifebuoy-line',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-ri-keyboard-line',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-ri-edit-line',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-ri-palette-line',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: 'Sugerir melhoria',
      icon: 'i-ri-lightbulb-line',
      click: () => {
        showMelhoria.value = true;
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-ri-book-line',
      click: openDocs,
    },
    {
      show: canInstall.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.INSTALL_APP'),
      icon: 'i-ri-install-line',
      click: async () => {
        await promptInstall();
        emit('close');
      },
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-ri-government-line',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-ri-shut-down-line',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    :ignore-click-outside="['[data-sidebar-profile-menu]']"
    open-upward
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        ref="triggerRef"
        class="flex gap-2 items-center p-1 text-left rounded-xl cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="
          () => {
            measure();
            toggle();
          }
        "
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <template #default="{ isOpen }">
      <TeleportWithDirection>
        <Transition
          appear
          enter-active-class="transition duration-[var(--motion-duration-enter)] ease-[var(--motion-ease-out)] origin-bottom"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-[var(--motion-duration-press)] ease-[var(--motion-ease-out)] origin-bottom"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            data-sidebar-profile-menu
            class="fixed z-[100]"
            :style="anchorStyle"
          >
            <DropdownBody
              class="overflow-y-auto bottom-0 w-80 ltr:left-0 rtl:right-0"
              :style="{ maxHeight: menuMaxHeight }"
            >
              <SidebarProfileMenuStatus />
              <DropdownSeparator />
              <template v-for="item in allowedMenuItems" :key="item.label">
                <CustomBrandPolicyWrapper
                  :show-on-custom-branded-instance="
                    item.showOnCustomBrandedInstance
                  "
                >
                  <DropdownItem v-if="item.show" v-bind="item" />
                </CustomBrandPolicyWrapper>
              </template>
            </DropdownBody>
          </div>
        </Transition>
      </TeleportWithDirection>
    </template>
  </DropdownContainer>
  <MelhoriaModal v-if="showMelhoria" @close="showMelhoria = false" />
</template>
